import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import PanelError from '@wandb/weave/common/components/elements/PanelError';
import _ from 'lodash';
import memoize from 'memoize-one';
import React, {useCallback} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Checkbox, Tab} from 'semantic-ui-react';
import useResizeObserver from 'use-resize-observer';

import {useSetHighlight} from '../../state/interactState/hooks';
import {useRunsData} from '../../state/runs/hooks';
import {getDefaultLegendTemplate} from '../../util/legend';
import * as PanelHelpers from '../../util/panelHelpers';
import {PanelProps} from '../../util/panelProps';
import {
  overrideBarColors,
  overrideBarTitles,
} from '../../util/plotHelpers/chart';
import {getPlotFontSize} from '../../util/plotHelpers/plotFontSize';
import {getPointsFromData} from '../../util/plotHelpers/plotHelpers';
import {Query} from '../../util/queryTypes';
import * as Run from '../../util/runs';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {LabeledOptionBlock as LabeledOption} from '../elements/LabeledOptionBlock';
import RangeInput from '../elements/RangeInput';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import {useStablePanelId} from '../Panel/PanelIDContext';
import PanelChartOptions from '../PanelChartOptions';
import {
  PanelChartModal,
  PanelChartPreview,
  PanelChartSettings,
} from '../PanelCommon';
import PanelLegend from '../PanelDisplayPreferences';
import {
  PanelExpressionOptions,
  parseExpressions,
} from '../PanelExpressionOptions';
import {PanelGroupingOptions} from '../PanelGrouping/PanelGroupingOptions';
import {useColorByMetrics} from '../PanelRunsLinePlot/useColorByMetrics';
import {PanelTitle} from '../PanelTitle/PanelTitle';
import PlotWarning from '../PlotWarning';
import ProjectFieldSelector from '../ProjectFieldSelector';
import BarChart from '../vis/BarChart/BarChart';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {InstrumentedLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {
  barChartTransformQuery,
  DEFAULT_BAR_CHART_MAX_GROUP_RUNS,
  defaultTitle,
  getTitleFromConfig,
  metricStringsToKeys,
} from './common';
import {BarChartConfig} from './types';

const ABSOLUTE_MAX_GROUP_RUNS = 1000;
const DEFAULT_BAR_LIMIT = 10;

type PanelBarChartProps = PanelProps<BarChartConfig>;

const getRunSets = (query: Query) => {
  return query.runSets != null
    ? query.runSets.map(rs => ({
        id: rs.id,
        grouping: rs.grouping,
        entityName: rs.entityName,
        projectName: rs.projectName,
        runNameTruncationType:
          rs.runNameTruncationType ?? TruncationType.Middle,
      }))
    : [];
};

function warnMaxPlotBarsShowingLimit(
  maxRuns: number,
  plotUnit: string,
  readOnly?: boolean
): React.ReactNode {
  return (
    <PlotWarning
      helpText={
        !readOnly
          ? `Click on the pencil icon (top right corner of this chart) to change the max number of ${plotUnit} displayed`
          : undefined
      }
      message={`Showing first ${maxRuns} ${plotUnit}`}
    />
  );
}

function warnRunSamplingWhenGroupedLimit(
  maxRunsSampled: number,
  readOnly?: boolean
): React.ReactNode {
  return (
    <PlotWarning
      helpText={
        !readOnly
          ? `Click on the pencil icon (top right corner of this chart) to change the number of runs sampled in group metrics`
          : undefined
      }
      message={`Computing group metrics from first ${maxRunsSampled} groups`}
    />
  );
}

const PanelBarChart: React.FC<PanelBarChartProps> = props => {
  const [runHighlight, setInternalRunHighlight] = React.useState<string | null>(
    null
  );

  const setHighlight = useSetHighlight();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memParseExpressions = useCallback(memoize(parseExpressions), [
    props.config.expressions,
  ]);

  const parsedExpressions = memParseExpressions(props.config.expressions);

  const panelQuery = barChartTransformQuery(
    props.pageQuery,
    props.config,
    parsedExpressions
  );
  const runsDataQuery = useRunsData(panelQuery);
  const {data} = runsDataQuery;

  const elementRef = React.useRef<HTMLDivElement>(null);
  const {height = 1} = useResizeObserver<HTMLDivElement>({
    ref: elementRef,
  });

  const setRunHighlight = (newRunHighlight: string | null) => {
    if (newRunHighlight == null) {
      setInternalRunHighlight(null);
      setHighlight('run:name', undefined);
    } else {
      setInternalRunHighlight(newRunHighlight);
      setHighlight('run:name', newRunHighlight);
    }
  };

  const {config} = props;

  const runSets = getRunSets(props.pageQuery);

  // We need to memoize this per component instance, rather than globally.
  // Matches logic in PanelRunsLinePlot
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memGetPointsFromData = useCallback(
    memoize(getPointsFromData, (a: any, b: any) => {
      return a[0] === b[0] && _.isEqual(a[1], b[1]);
    }),
    [getPointsFromData]
  );

  const {isSingleRun} = useSingleRunModeContext();

  const defaultLegendTemplate = getDefaultLegendTemplate({
    aggregateMetrics: config.aggregateMetrics ?? false,
    isGrouped: PanelHelpers.isGrouped(props.pageQuery, config) ?? false,
    isFullFidelity: false,
    isSmoothed: false,
    legendFields: config.legendFields ?? [],
    metrics: config.metrics ?? [],
    isSingleRun,
    type: 'bar',
    useRunsets: runSets != null && runSets.length > 1,
  });
  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const colorEachMetricDifferently = useColorByMetrics(config);
  let barData = memGetPointsFromData(data.filtered, {
    metricKeys: metricStringsToKeys(config.metrics ?? []),
    customRunColors: props.customRunColors,
    customRunNames: props.customRunNames,
    groupBy: config.groupBy ?? 'None',
    panelAggregate: config.aggregate,
    aggregateMetrics: config.aggregateMetrics ?? false,
    groupAgg: config.groupAgg,
    groupArea: config.groupArea,
    runSets,
    aggregateCalculations: [],
    colorEachMetricDifferently,
    boxPlot: config.plotStyle === 'boxplot',
    violinPlot: config.plotStyle === 'violin',
    legendTemplate: config.legendTemplate ?? defaultLegendTemplate,
    expressions: parsedExpressions.expressions ?? [],
    colorPaletteOption,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memOverrideColors = useCallback(memoize(overrideBarColors), [
    overrideBarColors,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memOverrideTitles = useCallback(memoize(overrideBarTitles), [
    overrideBarTitles,
  ]);

  if (config.overrideSeriesTitles != null) {
    barData = memOverrideTitles(
      barData,
      config.overrideSeriesTitles,
      !isSingleRun
    );
  }

  if (config.overrideColors != null) {
    barData = memOverrideColors(barData, config.overrideColors, !isSingleRun);
  }

  const renderConfig = () => {
    if (!data) {
      return <></>;
    }

    const dataTab = (
      <Tab.Pane as="div" className="form-grid">
        <LabeledOption
          label="Metric"
          option={
            <ProjectFieldSelector
              className="with-button"
              query={props.pageQuery}
              columns={['summary_metrics', 'config']}
              types={['number']}
              defaultKeys={[]}
              multi
              autoPick
              selection
              value={props.config.metrics}
              setValue={value => props.updateConfig({metrics: value})}
              searchByKeyAndText
            />
          }
        />
        <LabeledOption
          label="Range"
          option={
            <RangeInput
              disabled={
                props.config.metrics == null ||
                props.config.metrics.length === 0
              }
              onMinChange={newVal => {
                props.updateConfig({xAxisMin: newVal});
              }}
              onMaxChange={newVal => {
                props.updateConfig({xAxisMax: newVal});
              }}
              minValue={props.config.xAxisMin}
              maxValue={props.config.xAxisMax}
            />
          }
        />
        <LabeledOption
          label="Vertical"
          helpText="Plot vertical lines"
          option={
            <Checkbox
              toggle
              checked={config.vertical}
              onClick={(e, value) =>
                props.updateConfig({
                  vertical: value.checked,
                })
              }
            />
          }
        />
        <LabeledOption
          label={`Max runs to show`}
          option={
            <ModifiedDropdown
              lazyLoad
              className="no-wrap-options"
              value={config.limit || 10}
              style={{minWidth: 60}}
              selection
              options={[2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30, 50, 100].map(n => ({
                value: n,
                text: `${n} runs`,
              }))}
              onChange={(e, {value}) => {
                if (typeof value === 'number') {
                  props.updateConfig({
                    limit: value,
                  });
                }
              }}
            />
          }
        />
        {((config.metrics?.length && config.metrics?.length >= 2) ||
          barData.length > (config.barLimit ?? DEFAULT_BAR_LIMIT)) && (
          <LabeledOption
            label={`Max bars to show`}
            option={
              <ModifiedDropdown
                lazyLoad
                className="no-wrap-options"
                value={config.barLimit ?? DEFAULT_BAR_LIMIT}
                style={{minWidth: 60}}
                selection
                options={[
                  2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30, 50, 100, 1000,
                ].map(n => ({
                  value: n,
                  text: `${n} bars`,
                }))}
                onChange={(e, {value}) => {
                  if (typeof value === 'number') {
                    props.updateConfig({
                      barLimit: value,
                    });
                  }
                }}
              />
            }
          />
        )}
      </Tab.Pane>
    );

    const legendTab = (
      <Tab.Pane as="div" className="form-grid">
        <PanelLegend
          type={'bars'}
          config={config}
          updateConfig={props.updateConfig}
          pageQuery={props.pageQuery}
          defaultTitle={defaultTitle(props.config)}
          editableLegendSeries={barData}
          defaultLegendTemplate={defaultLegendTemplate}
        />
      </Tab.Pane>
    );

    const expressionsTab = (
      <Tab.Pane as="div" className="form-grid">
        <PanelExpressionOptions
          type={'bars'}
          config={config}
          updateConfig={props.updateConfig}
          availableExpressionVarNames={[]}
          exampleIdentifier={Run.rawConfigKeyDisplayName(
            (config.metrics ? config.metrics[0] : undefined) ?? 'summary:acc'
          )}
        />
      </Tab.Pane>
    );

    const chartTab = (
      <Tab.Pane as="div" className="form-grid">
        <PanelChartOptions
          type={'bars'}
          config={config}
          updateConfig={props.updateConfig}
          pageQuery={props.pageQuery}
        />
      </Tab.Pane>
    );

    const settingsPanes = [
      {
        menuItem: 'Data',
        render: () => dataTab,
      },
      {
        menuItem: 'Grouping',
        render: () => groupingTab,
      },
      {
        menuItem: 'Chart',
        render: () => chartTab,
      },
      {
        menuItem: 'Legend',
        render: () => legendTab,
      },
      {
        menuItem: 'Expressions',
        render: () => expressionsTab,
      },
    ];

    const groupingTab = (
      <Tab.Pane as="div" className="form-grid">
        <PanelGroupingOptions
          type={'bars'}
          config={config}
          disabled={!config.metrics || config.metrics.length === 0}
          isGrouped={PanelHelpers.isGrouped(props.pageQuery, config) || false}
          updateConfig={props.updateConfig}
          defaultMaxGroupRuns={DEFAULT_BAR_CHART_MAX_GROUP_RUNS}
          absoluteMaxGroupRuns={ABSOLUTE_MAX_GROUP_RUNS}
          totalRuns={data.totalRuns}
          usingExpressions={false}
          pageQuery={props.pageQuery}
        />
      </Tab.Pane>
    );

    return (
      <PanelChartModal>
        <PanelChartPreview>{renderGraph()}</PanelChartPreview>
        <PanelChartSettings>
          <Tab
            panes={settingsPanes}
            menu={{
              secondary: true,
              pointing: true,
              className: 'chart-settings-tab-menu',
            }}
          />
        </PanelChartSettings>
      </PanelChartModal>
    );
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memWarnRunSamplingWhenGroupedLimit = useCallback(
    memoize(warnRunSamplingWhenGroupedLimit),
    [warnRunSamplingWhenGroupedLimit]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memWarnMaxPlotBarsShowingLimit = useCallback(
    memoize(warnMaxPlotBarsShowingLimit),
    [warnMaxPlotBarsShowingLimit]
  );

  const stablePanelId = useStablePanelId();

  const renderGraph = () => {
    let errorMessage;
    let errorLogMessage;

    const maxBars = config.barLimit ?? DEFAULT_BAR_LIMIT;

    let legendPrefix;

    if (PanelHelpers.isGrouped(props.pageQuery, config)) {
      // show user warning about group subsampling when the total runs visible in the table
      // exceed the number of runs sampled to compute group metrics in the chart
      if (
        props.data.totalRuns >
        (config.groupRunsLimit ?? DEFAULT_BAR_CHART_MAX_GROUP_RUNS)
      ) {
        legendPrefix = memWarnRunSamplingWhenGroupedLimit(
          config.groupRunsLimit ?? DEFAULT_BAR_CHART_MAX_GROUP_RUNS,
          props.readOnly
        );
        // show user warning about max groups if the total groups visible in the table exceed
        // the maximum number of runs displayed in the chart--note this happens less often and is
        // easier to notice than the previous case
      } else if (barData.length > maxBars) {
        legendPrefix = memWarnMaxPlotBarsShowingLimit(
          maxBars,
          'bars',
          props.readOnly
        );
      }
      // show user warning about plot line limit: there are more runs visible in the table than can be displayed
      // as plot lines in the chart
    } else if (barData.length > maxBars) {
      legendPrefix = memWarnMaxPlotBarsShowingLimit(
        maxBars,
        'bars',
        props.readOnly
      );
    }

    if (config.metrics == null || config.metrics.length === 0) {
      errorMessage = 'Select a metric to visualize in this line chart.';
      errorLogMessage = 'Select a metric to visualize in this line chart.';
    } else if (data.filtered.length === 0) {
      errorMessage = (
        <>
          Select runs that logged{' '}
          {config.metrics?.map(Run.rawConfigKeyDisplayName).join(',')} <br />
          to visualize data in this bar chart.
        </>
      );
      errorLogMessage = `Select runs that logged ${config.metrics?.map(Run.rawConfigKeyDisplayName).join(',')} to visualize data in this bar chart.`;
    }

    const highlight = _.find(barData, point => point.uniqueId === runHighlight);

    const fontSize = getPlotFontSize(config.fontSize ?? 'auto', height);

    return (
      <div className="chart" ref={elementRef}>
        <PanelTitle
          title={getTitleFromConfig(props.config)}
          fontSize={fontSize}
        />
        {props.loading && <Loader name={'panel-bar-chart-loader'} />}
        {!props.loading && (
          <div className="chart-with-warning">
            {legendPrefix}

            <div className="chart-content" data-test="chart-content">
              {!data.loading && errorMessage != null ? (
                <PanelError
                  panelID={stablePanelId}
                  panelViewType="Bar Chart"
                  message={errorMessage}
                  logErrorMessage={
                    errorLogMessage || 'Bar chart error occurred'
                  }
                />
              ) : (
                <BarChart
                  bars={barData}
                  vertical={config.vertical}
                  boxPlot={
                    PanelHelpers.isGrouped(props.pageQuery, config) &&
                    config.plotStyle === 'boxplot'
                  }
                  violinPlot={
                    PanelHelpers.isGrouped(props.pageQuery, config) &&
                    config.plotStyle === 'violin'
                  }
                  maxBars={maxBars}
                  showAllLabels={true}
                  min={config.xAxisMin}
                  max={config.xAxisMax}
                  highlight={highlight}
                  mouseOver={(event, bar) =>
                    setRunHighlight(bar.uniqueId || null)
                  }
                  fontSize={fontSize}
                  mouseOut={() => setRunHighlight(null)}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (props.configMode) {
    return renderConfig();
  } else {
    return renderGraph();
  }
};

export default PanelBarChart;
